'use client';

import type { ReactNode } from 'react';
import { PlusIcon, TargetIcon } from '@phosphor-icons/react';

import { getGoalIcon, getProgressColor } from '@/components/metas/helpers';
import { Button } from '@/components/ui/button';
import Link from '@/components/ui/Link';
import { useGoalsOverview } from '@/hooks/queries/useGoalsOverview';
import { useCompactCurrency } from '@/hooks/useCompactCurrency';
import { trackEvent } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import type { GoalSummary } from '@/types';
import { WidgetCard, widgetState } from './WidgetCard';

const MAX_GOALS = 4;

/**
 * W5 · Metas — the user's goals and their progress (replaced the Anéis do ciclo
 * allocation rings, product decision 2026-07-14): reuses the metas page's icon +
 * health-color helpers so the two surfaces agree. No metas yet → a create CTA
 * straight to /metas/new. One component serves the desktop card and the mobile
 * Panorama slide.
 */
export function MetasWidget(): ReactNode {
  const { data, error, isLoading } = useGoalsOverview();

  return (
    <WidgetCard
      eyebrow="Metas"
      action={{ label: 'Ver', href: '/metas' }}
      // The no-goals state renders its own CTA body — never the generic notice.
      state={widgetState(data, error, isLoading, () => false)}
    >
      {data && (data.goals.length === 0 ? <CreateGoalCta /> : <GoalList goals={data.goals} />)}
    </WidgetCard>
  );
}

function trackCreateGoalClick(): void {
  trackEvent('new_home.widget_action', { widget: 'metas', action: 'create_goal' });
}

function trackGoalClick(): void {
  trackEvent('new_home.widget_action', { widget: 'metas', action: 'open_goal' });
}

function CreateGoalCta(): ReactNode {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 py-6 text-center">
      <span className="grid size-11 place-items-center rounded-full bg-brand-50 text-primary">
        <TargetIcon size={22} weight="duotone" aria-hidden />
      </span>
      <div>
        <p className="text-sm font-medium text-text-primary">Sem metas ainda</p>
        <p className="mt-0.5 text-xs text-text-tertiary">
          Defina um objetivo e acompanhe o progresso aqui
        </p>
      </div>
      <Button asChild size="sm" className="rounded-full">
        <Link href="/metas/new" onClick={trackCreateGoalClick}>
          <PlusIcon size={14} weight="bold" aria-hidden />
          Criar meta
        </Link>
      </Button>
    </div>
  );
}

function GoalList({ goals }: { goals: GoalSummary[] }): ReactNode {
  const visible = goals.slice(0, MAX_GOALS);
  const hiddenCount = goals.length - visible.length;

  return (
    <div className="mt-3 flex h-full flex-col">
      <ul className="space-y-3.5">
        {visible.map((goal) => (
          <GoalRow key={goal.id} goal={goal} />
        ))}
      </ul>
      {hiddenCount > 0 && (
        <Link
          href="/metas"
          className="mt-3 text-xs font-medium text-text-tertiary transition-colors hover:text-text-primary"
        >
          +{hiddenCount} {hiddenCount === 1 ? 'meta' : 'metas'}
        </Link>
      )}
    </div>
  );
}

function GoalRow({ goal }: { goal: GoalSummary }): ReactNode {
  // Prototype-style compact BRL ("R$ 7,5 mil") — privacy-aware, masked first.
  const compactCurrency = useCompactCurrency({ style: 'mil', maximumFractionDigits: 1 });
  const Icon = getGoalIcon(goal.icon, goal.type);
  // The bar clamps to 100%; the number shows the real (rounded) progress.
  const barPercent = Math.max(0, Math.min(100, goal.progressPercent));

  return (
    <li>
      <Link
        href="/metas"
        onClick={trackGoalClick}
        className="group flex items-center gap-3"
        aria-label={`Meta ${goal.name} · ${Math.round(goal.progressPercent)}%`}
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-surface-sunken text-text-secondary">
          <Icon size={15} weight="duotone" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium text-text-primary group-hover:underline">
              {goal.name}
            </span>
            <span className="shrink-0 font-mono text-xs text-text-secondary tabular-nums">
              {Math.round(goal.progressPercent)}%
            </span>
          </span>
          <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
            <span
              data-testid="goal-bar"
              className={cn('block h-full rounded-full', getProgressColor(goal.healthStatus))}
              style={{ width: `${barPercent}%` }}
            />
          </span>
          <span className="mt-1 block font-mono text-2xs text-text-tertiary tabular-nums">
            {compactCurrency(goal.currentAmount)} de {compactCurrency(goal.targetAmount)}
          </span>
        </span>
      </Link>
    </li>
  );
}
